import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, Container, Card, Badge, Modal, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Trash2 as FaTrash, Pencil as FaEdit, TreePalm as FaUmbrellaBeach } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import api from '../../api/axios';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import MyCalendar from '../../components/MyCalendar';
import GradientButton from '../../components/GradientButton';

import {
  createVacation,
  deleteVacation,
  fetchVacations,
  fetchVacationPool,
  updateVacation
} from '../../actions/vacationActions';

import {
  CREATE_VACATION_RESET,
  DELETE_VACATION_RESET,
  UPDATE_VACATION_RESET
} from '../../constants/vacationConstants';

const VacationList = () => {
  const dispatch = useDispatch();

  const { loading, error, vacations, pages } = useSelector(state => state.vacationList);
  const { pool } = useSelector(state => state.vacationPool);
  const [currentPage, setCurrentPage] = useState(1);
  const [allVacations, setAllVacations] = useState([]);
  const { loading: loadingCreate, error: errorCreate, success: successCreate } = useSelector(state => state.vacationCreate);
  const { loading: loadingDelete, error: errorDelete, success: successDelete } = useSelector(state => state.vacationDelete);
  const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = useSelector(state => state.vacationUpdate);

  const [dateRange, setDateRange] = useState([null, null]);
  const [vacationType, setVacationType] = useState('wypoczynkowy');
  const [showModal, setShowModal] = useState(false);
  const [vacationToDelete, setVacationToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [vacationToEdit, setVacationToEdit] = useState(null);
  const [editError, setEditError] = useState('');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  // Funkcja do ładowania wszystkich urlopów dla kalendarza
  const loadAllVacationsForCalendar = async () => {
    try {
      let allVacs = [];
      for (let page = 1; page <= (pages || 1); page++) {
        const response = await api.get(`/vacation/my-vacations/?page=${page}`);
        allVacs = [...allVacs, ...(response.data.results || [])];
      }
      setAllVacations(allVacs);
    } catch (error) {
      console.error('Błąd ładowania urlopów dla kalendarza:', error);
    }
  };

  const capitalize = (type) => {
    return `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
  };

  const calculateWorkingDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return count;
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  };

  const getVacationTypeBadge = (type) => {
    const badges = {
      'wypoczynkowy': { bg: 'primary' },
      'bezpłatny': { bg: 'danger' },
      'okolicznościowy': { bg: 'warning' }
    };
    return badges[type] || badges['wypoczynkowy'];
  };

  useEffect(() => {
    dispatch(fetchVacations(1));  // Wczytaj stronę 1
    dispatch(fetchVacationPool());
    
    // Wczytaj wszystkie strony dla kalendarza
    loadAllVacationsForCalendar();
  }, [dispatch, pages]);

  useEffect(() => {
    if (successCreate) {
      dispatch(fetchVacations(currentPage));
      dispatch(fetchVacationPool());
      loadAllVacationsForCalendar();
      setDateRange([null, null]);
      setVacationType('wypoczynkowy');
      const timer = setTimeout(() => {
        dispatch({ type: CREATE_VACATION_RESET });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successCreate, dispatch, currentPage]);

  useEffect(() => {
    if (errorCreate) {
      const timer = setTimeout(() => {
        dispatch({ type: CREATE_VACATION_RESET });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorCreate, dispatch]);

  useEffect(() => {
    if (successDelete) {
      dispatch(fetchVacationPool());
      loadAllVacationsForCalendar();
      const timer = setTimeout(() => {
        dispatch({ type: DELETE_VACATION_RESET });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successDelete, dispatch]);

  useEffect(() => {
    if (successUpdate) {
      dispatch(fetchVacationPool());
      loadAllVacationsForCalendar();
      const timer = setTimeout(() => {
        dispatch({ type: UPDATE_VACATION_RESET });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successUpdate, dispatch]);

  useEffect(() => {
    dispatch(fetchVacations(currentPage));
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (errorUpdate) {
      setEditError(errorUpdate);
    }

    if (successUpdate) {
      setShowEditModal(false);
      setEditError('');
      dispatch(fetchVacations(currentPage));
      dispatch(fetchVacationPool());
      loadAllVacationsForCalendar();

      const timer = setTimeout(() => {
        dispatch({ type: UPDATE_VACATION_RESET });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorUpdate, successUpdate, dispatch, currentPage]);

  const submitHandler = (e) => {
    e.preventDefault();
    const [startDate, endDate] = dateRange;
    if (startDate && endDate && vacationType) {
      dispatch(createVacation(formatDateForInput(startDate), formatDateForInput(endDate), vacationType));
    }
  };

  const handleCalendarChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleFormDateChange = (e) => {
    const { name, value } = e.target;
    const [start, end] = dateRange;
    const newDate = value ? new Date(value) : null;

    if (name === 'startDate') {
      setDateRange([newDate, end]);
    } else {
      setDateRange([start, newDate]);
    }
  };

  const handleShowModal = (id) => {
    setVacationToDelete(id);
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    if (vacationToDelete) {
      dispatch(deleteVacation(vacationToDelete));
    }
    setShowModal(false);
  };

  const handleShowEditModal = (vacation) => {
    setVacationToEdit({ ...vacation });
    setEditError('');
    setShowEditModal(true);
  };

  const handleUpdateChange = (e) => {
    setVacationToEdit({
      ...vacationToEdit,
      [e.target.name]: e.target.value,
    });
  };

  const handleConfirmUpdate = () => {
    const { start_date, end_date } = vacationToEdit;

    if (!start_date || !end_date) {
      setEditError('Wszystkie pola muszą być uzupełnione.');
      return;
    }

    if (start_date > end_date) {
      setEditError('Data rozpoczęcia nie może być po dacie zakończenia.');
      return;
    }

    setEditError('');
    dispatch(updateVacation(vacationToEdit));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [startDate, endDate] = dateRange;

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%'
    }}>
      <Container fluid className="px-0 mx-0 px-md-3 scalable-column">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4"
        >
          <h1 className="pub-h2 mb-2">
            Zarządzanie urlopami
          </h1>
          <p className="pub-muted">Planuj swój czas wolny z łatwością</p>
        </motion.div>

        {error && <Message variant='danger'>{error}</Message>}
        {loading && <Loader />}

        <div className="row g-2 g-md-4">
          
          {/* 1. KALENDARZ */}
          {/* Mobile: 1, Desktop: 1 (Rząd 1, Kol 1) */}
          <div className="col-12 col-lg-8 order-1 order-lg-1">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <MyCalendar
                vacations={allVacations || []}
                dateRange={dateRange}
                onDateChange={handleCalendarChange}
              />
            </motion.div>
          </div>

          {/* 2. DODAJ NOWY URLOP */}
          {/* Mobile: 2, Desktop: 4 (Rząd 2, Kol 2) */}
          <div className="col-12 col-lg-4 order-2 order-lg-2">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="shadow-lg border-0" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{
                  background: 'var(--pub-bg-alt)',
                  border: 'none',
                  padding: '1.5rem'
                }}>
                  <h5 className="mb-0">
                    {/* <FaCalendarAlt className="me-2" /> */}
                    Dodaj nowy urlop
                  </h5>
                </Card.Header>
                <Card.Body style={{ padding: '1.5rem' }}>
                  <Form onSubmit={submitHandler}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Początek urlopu</Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={formatDateForInput(startDate)}
                        onChange={handleFormDateChange}
                        disabled={pool.questionnaire_incomplete}
                        required
                        style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                        className="py-2"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Koniec urlopu</Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={formatDateForInput(endDate)}
                        onChange={handleFormDateChange}
                        disabled={pool.questionnaire_incomplete}
                        required
                        style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                        className="py-2"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium">Rodzaj urlopu</Form.Label>
                      <Form.Select
                        value={vacationType}
                        onChange={(e) => setVacationType(e.target.value)}
                        disabled={pool.questionnaire_incomplete}
                        required
                        style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                        className="py-2"
                      >
                        <option value="wypoczynkowy">Wypoczynkowy</option>
                        <option value="bezpłatny">Bezpłatny</option>
                        <option value="okolicznościowy">Okolicznościowy</option>
                      </Form.Select>
                    </Form.Group>
                    <div className="d-flex justify-content-center"> 
                      <GradientButton
                        type="submit"
                        disabled={pool.questionnaire_incomplete || pool.available_days <= 0}
                        gradient='blue'
                      >
                        Dodaj urlop
                      </GradientButton>
                    </div>
                  </Form>

                  {/* Messages */}
                  <div className="mt-3">
                    {loadingCreate && <Loader />}
                    {errorCreate && <Alert variant="danger" className="border-0 mt-3" style={{ borderRadius: '10px' }}>{errorCreate}</Alert>}
                    {successCreate && <Alert variant="success" className="border-0 mt-3" 
                    style={{ 
                          borderRadius: '15px',
                          background: 'var(--pub-bg-alt)'
                        }} >✅ Urlop został dodany</Alert>}
                    {loadingUpdate && <Loader />}
                    {successUpdate && <Alert variant="success" className="border-0 mt-3"                         
                    style={{ 
                          borderRadius: '15px',
                          background: 'var(--pub-bg-alt)'
                        }}>✅ Urlop został zaktualizowany</Alert>}
                    {loadingDelete && <Loader />}
                    {errorDelete && <Alert variant="danger" className="border-0 mt-3" style={{ borderRadius: '10px' }}>{errorDelete}</Alert>}
                    {successDelete && <Alert variant="success" className="border-0 mt-3"  
                    style={{ 
                          borderRadius: '15px',
                          background: 'var(--pub-bg-alt)'
                        }}>✅ Urlop został usunięty</Alert>}
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          </div>

          {/* 3. HISTORIA URLOPÓW */}
          {/* Mobile: 4, Desktop: 3 (Rząd 2, Kol 1) */}
          <div className="col-12 col-lg-8 order-4 order-lg-3">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="shadow-lg border-0" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{
                  background: 'var(--pub-bg-alt)',
                  border: 'none',
                  padding: '1.5rem'
                }}>
                  <h4 className="mb-0">
                    {/* <FaChartLine className="me-2" /> */}
                    Historia urlopów
                  </h4>
                </Card.Header>
                <Card.Body style={{ padding: '1rem 0.5rem' }}>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th className="border-0 text-muted" style={{ fontSize: '0.85rem' }}>Od</th>
                          <th className="border-0 text-muted" style={{ fontSize: '0.85rem' }}>Do</th>
                          <th className="border-0 text-muted text-center" style={{ fontSize: '0.85rem' }}>Dni</th>
                          <th className="border-0 text-muted d-none d-md-table-cell" style={{ fontSize: '0.85rem' }}>Typ</th>
                          <th className="border-0 text-muted text-center" style={{ fontSize: '0.85rem' }}>Akcje</th>
                        </tr>
                      </thead>
                      <tbody>
                        <AnimatePresence>
                          {vacations?.length > 0 ? (
                            vacations.map((vacation, index) => {
                              const badge = getVacationTypeBadge(vacation.vacation_type);
                              const diffDays = calculateWorkingDays(vacation.start_date, vacation.end_date);

                              return (
                                <motion.tr
                                  key={vacation.id}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 20 }}
                                  transition={{ delay: index * 0.1 }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {/* <FaCalendarAlt className="text-primary me-1 d-none d-sm-inline" /> */}
                                      <span className="fw-medium" style={{ fontSize: '0.9rem' }}>{formatDate(vacation.start_date)}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="fw-medium" style={{ fontSize: '0.9rem' }}>{formatDate(vacation.end_date)}</span>
                                  </td>
                                  <td className="text-center fw-bold text-muted" style={{ fontSize: '0.9rem' }}>{diffDays}</td>
                                  <td className="d-none d-md-table-cell">
                                    <Badge bg={badge.bg} className="px-2 py-1" style={{ fontSize: '0.8rem' }}>
                                      {capitalize(vacation.vacation_type)}
                                    </Badge>
                                  </td>
                                  <td className="text-center">
                                    <div className="btn-group" role="group">
                                      <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => handleShowEditModal(vacation)}
                                        style={{ borderRadius: '10px 0 0 10px', padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                      >
                                        <FaEdit />
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleShowModal(vacation.id)}
                                        style={{ borderRadius: '0 10px 10px 0', padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </div>
                                  </td>
                                </motion.tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-5 text-muted">
                                <FaUmbrellaBeach size={50} className="mb-3 opacity-25" />
                                <p>Brak wniosków urlopowych</p>
                              </td>
                            </tr>
                          )}
                        </AnimatePresence>
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav>
                        <ul className="pagination">
                          {[...Array(pages).keys()].map((x) => (
                            <li key={x + 1} className={`page-item ${x + 1 === currentPage ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(x + 1)}
                                style={{ borderRadius: '10px', margin: '0 5px' }}
                              >
                                {x + 1}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </motion.div>
          </div>
          {/* 4. TWOJA PULA URLOPOWA */}
          {/* Mobile: 3, Desktop: 2 (Rząd 1, Kol 2) */}
          <div className="col-12 col-lg-4 order-3 order-lg-4">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="shadow-lg border-0" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{
                  background: 'var(--pub-bg-alt)',
                  border: 'none',
                  padding: '1.5rem'
                }}>
                  <h5 className="mb-0">
                    Twoja pula urlopowa
                  </h5>
                </Card.Header>
                <Card.Body style={{ padding: '1.5rem' }}>
                  {pool.questionnaire_incomplete ? (
                    <Alert variant="warning" className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                      <div className="text-center">
                        <div className="mb-3" style={{ fontSize: '3rem' }}>⚠️</div>
                        <h6 className="fw-bold">Brak dostępu do puli urlopowej</h6>
                        <p className="mb-3 small">
                          Aby dodawać urlopy, musisz najpierw wypełnić kwestionariusz urlopowy.
                        </p>
                        <Link to="/home-screen" className="btn btn-warning btn-sm" style={{ borderRadius: '10px' }}>
                          Przejdź do kwestionariusza
                        </Link>
                      </div>
                    </Alert>
                  ) : (
                    <>
                      <div className="row text-center g-3 mb-3">
                        <div className="col-4">
                          <div className="p-0 p-md-3" style={{
                            background: 'var(--pub-bg-alt)',
                            borderRadius: '15px'
                          }}>
                            <div style={{ fontSize: '2rem', fontFamily: 'var(--pub-serif)', color: 'var(--pub-navy)' }}>
                              {pool.granted_days || 0}
                            </div>
                            <small className="pub-small">Przyznane</small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="p-0 p-md-3" style={{
                            background: 'var(--pub-bg-alt)',
                            borderRadius: '15px'
                          }}>
                            <div style={{ fontSize: '2rem', fontFamily: 'var(--pub-serif)', color: 'var(--pub-navy)' }}>
                              {pool.used_days + pool.vacation_used || 0}
                            </div>
                            <small className="pub-small">Użyte</small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="p-0 p-md-3" style={{
                            background: 'var(--pub-bg-alt)',
                            borderRadius: '15px'
                          }}>
                            <div style={{ fontSize: '2rem', fontFamily: 'var(--pub-serif)', color: 'var(--pub-navy)' }}>
                              {pool.available_days || 0}
                            </div>
                            <small className="pub-small">Dostępne</small>
                          </div>
                        </div>
                      </div>

                      {pool.available_days <= 0 && (
                        <Alert variant="danger" className="border-0 text-center" style={{ borderRadius: '15px' }}>
                          <small>❌ Brak dostępnych dni urlopu</small>
                        </Alert>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </motion.div>
          </div>
        </div> 

        {/* DELETE MODAL */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
            <Modal.Title>Potwierdzenie usunięcia</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '2rem' }}>
            <div className="text-center">
              <p className="mb-0">Czy na pewno chcesz usunąć ten wniosek urlopowy?</p>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ border: 'none', paddingTop: 0 }}>
            <Button
              variant="light"
              onClick={() => setShowModal(false)}
              style={{ borderRadius: '10px' }}
            >
              Anuluj
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              style={{ borderRadius: '10px' }}
            >
              Usuń
            </Button>
          </Modal.Footer>
        </Modal>

        {/* EDIT MODAL */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
            <Modal.Title>Edycja urlopu</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '2rem' }}>
            {editError && <Alert variant="danger" className="border-0" style={{ borderRadius: '10px' }}>{editError}</Alert>}
            {vacationToEdit && (
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Data rozpoczęcia</Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={vacationToEdit.start_date}
                    onChange={handleUpdateChange}
                    style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                    className="py-2"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Data zakończenia</Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={vacationToEdit.end_date}
                    onChange={handleUpdateChange}
                    style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                    className="py-2"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Typ urlopu</Form.Label>
                  <Form.Select
                    name="vacation_type"
                    value={vacationToEdit.vacation_type}
                    onChange={handleUpdateChange}
                    style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                    className="py-2"
                  >
                    <option value="wypoczynkowy">Wypoczynkowy</option>
                    <option value="bezpłatny">Bezpłatny</option>
                    <option value="okolicznościowy">Okolicznościowy</option>
                  </Form.Select>
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer style={{ border: 'none', paddingTop: 0 }}>
            <Button
              variant="light"
              onClick={() => setShowEditModal(false)}
              style={{ borderRadius: '10px' }}
            >
              Anuluj
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmUpdate}
              style={{ borderRadius: '10px' }}
            >
              Zapisz zmiany
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default VacationList;